/**
 * Utilitários para manipulação de preços de produtos baseados em planos
 * 
 * Com a nova arquitetura, o preço de um produto sempre vem de seus planos,
 * não mais do campo product.price diretamente.
 */

import CurrencyFormatter from './CurrencyFormatter';

/**
 * Obtém o preço do produto baseado no plano padrão
 * @param {Object} product - Objeto do produto
 * @returns {number} - Preço do produto (0 se não houver planos)
 */
export const getProductPrice = (product) => {
  if (!product) return 0;
  
  // Se o produto tem planos, buscar o plano padrão ou o primeiro
  if (product.plans && product.plans.length > 0) {
    const defaultPlan = product.plans.find(plan => plan.is_default) || product.plans[0];
    return parseFloat(defaultPlan.price) || 0;
  }
  
  // Fallback: usar product.price durante período de transição (já convertido do backend)
  // Se product.price vier como string formatada (ex: "R$ 600,00"), extrair o número
  if (typeof product.price === 'string' && product.price.includes('R$')) {
    const numericValue = parseFloat(product.price.replace(/[R$\s.,]/g, '').replace(',', '.'));
    return numericValue * 100; // Converter para centavos para consistência
  }
  return parseFloat(product.price) || 0;
};

/**
 * Obtém o plano padrão de um produto
 * @param {Object} product - Objeto do produto
 * @returns {Object|null} - Plano padrão ou null
 */
export const getDefaultPlan = (product) => {
  if (!product || !product.plans || product.plans.length === 0) {
    return null;
  }
  
  return product.plans.find(plan => plan.is_default) || product.plans[0];
};

/**
 * Obtém faixa de preços para produtos com múltiplos planos
 * @param {Object} product - Objeto do produto
 * @returns {string} - String formatada com faixa de preços
 */
export const getProductPriceRange = (product) => {
  if (!product || !product.plans || product.plans.length === 0) {
    // Usar a mesma lógica de getProductPrice para o fallback
    let fallbackPrice = 0;
    if (typeof product?.price === 'string' && product.price.includes('R$')) {
      const numericValue = parseFloat(product.price.replace(/[R$\s.,]/g, '').replace(',', '.'));
      fallbackPrice = numericValue * 100; // Converter para centavos
    } else {
      fallbackPrice = parseFloat(product?.price) || 0;
    }
    return formatCurrency(fallbackPrice);
  }
  
  const prices = product.plans.map(plan => parseFloat(plan.price) || 0);
  const minPrice = Math.min(...prices);
  const maxPrice = Math.max(...prices);
  
  if (minPrice === maxPrice) {
    return formatCurrency(minPrice);
  }
  
  return `${formatCurrency(minPrice)} - ${formatCurrency(maxPrice)}`;
};

/**
 * Verifica se um produto tem múltiplos planos
 * @param {Object} product - Objeto do produto
 * @returns {boolean}
 */
export const hasMultiplePlans = (product) => {
  return product?.plans?.length > 1;
};

/**
 * Verifica se um produto tem pelo menos um plano
 * @param {Object} product - Objeto do produto
 * @returns {boolean}
 */
export const hasPlans = (product) => {
  return product?.plans?.length > 0;
};

/**
 * @deprecated Usar CurrencyFormatter.centsToFormatted() no lugar
 * Formata valor para moeda brasileira
 * @param {number} value - Valor em centavos
 * @returns {string} - Valor formatado
 */
export const formatCurrency = (value) => {
  return CurrencyFormatter.centsToFormatted(value);
};

/**
 * Retorna preço formatado e label adequado para exibição
 * - 1 plano: { label: null, price: "R$ 75,00" }
 * - Múltiplos planos: { label: "A partir de", price: "R$ 10,00" }
 * @param {Object} product - Objeto do produto
 * @returns {Object} { label, price, priceValue }
 */
export const getProductPriceDisplay = (product) => {
  if (!product) return { label: null, price: formatCurrency(0), priceValue: 0 };

  const activePlans = product.plans?.filter(p => p.status === 'active' || !p.status) || [];

  if (activePlans.length > 1) {
    const prices = activePlans.map(p => parseFloat(p.price) || 0).filter(p => p > 0);
    const minPrice = Math.min(...prices);
    const maxPrice = Math.max(...prices);
    if (minPrice === maxPrice) {
      return { label: null, price: formatCurrency(minPrice), priceValue: minPrice };
    }
    return { label: `de ${formatCurrency(minPrice)} até`, price: formatCurrency(maxPrice), priceValue: minPrice };
  }

  const price = getProductPrice(product);
  return { label: null, price: formatCurrency(price), priceValue: price };
};

/**
 * Calcula comissão baseada no preço do produto
 * @param {Object} product - Objeto do produto
 * @param {number} commissionRate - Taxa de comissão (%)
 * @param {string} commissionType - Tipo: 'percentage' ou 'fixed'
 * @returns {number} - Valor da comissão
 */
export const calculateCommission = (product, commissionRate, commissionType = 'percentage') => {
  const productPrice = getProductPrice(product);
  
  if (commissionType === 'percentage') {
    return (productPrice * commissionRate) / 100;
  }
  
  return commissionRate; // Valor fixo
};

/**
 * Obtém informações resumidas sobre planos de um produto
 * @param {Object} product - Objeto do produto
 * @returns {Object} - Resumo dos planos
 */
export const getProductPlansInfo = (product) => {
  if (!hasPlans(product)) {
    // Usar a mesma lógica de conversão para fallback
    let fallbackPrice = 0;
    if (typeof product?.price === 'string' && product.price.includes('R$')) {
      const numericValue = parseFloat(product.price.replace(/[R$\s.,]/g, '').replace(',', '.'));
      fallbackPrice = numericValue * 100; // Converter para centavos
    } else {
      fallbackPrice = parseFloat(product?.price) || 0;
    }
    
    return {
      hasPlans: false,
      totalPlans: 0,
      defaultPlan: null,
      priceRange: formatCurrency(fallbackPrice),
      minPrice: fallbackPrice,
      maxPrice: fallbackPrice
    };
  }
  
  const plans = product.plans;
  const prices = plans.map(plan => parseFloat(plan.price) || 0);
  const defaultPlan = getDefaultPlan(product);
  
  return {
    hasPlans: true,
    totalPlans: plans.length,
    defaultPlan: defaultPlan,
    priceRange: getProductPriceRange(product),
    minPrice: Math.min(...prices),
    maxPrice: Math.max(...prices),
    plans: plans
  };
};

/**
 * Valida se um produto tem configuração de preços válida
 * @param {Object} product - Objeto do produto
 * @returns {Object} - Resultado da validação
 */
export const validateProductPricing = (product) => {
  const info = getProductPlansInfo(product);
  
  if (!info.hasPlans) {
    return {
      isValid: !!product?.price,
      errors: product?.price ? [] : ['Produto deve ter pelo menos um plano ou um preço definido'],
      warnings: ['Produto não possui planos. Considere adicionar pelo menos um plano.']
    };
  }
  
  const errors = [];
  const warnings = [];
  
  // Verificar se há pelo menos um plano com preço válido
  const validPlans = info.plans.filter(plan => plan.price && parseFloat(plan.price) > 0);
  if (validPlans.length === 0) {
    errors.push('Pelo menos um plano deve ter um preço válido');
  }
  
  // Verificar se há plano padrão
  if (!info.defaultPlan) {
    warnings.push('Nenhum plano está marcado como padrão. O primeiro plano será usado como padrão.');
  }
  
  return {
    isValid: errors.length === 0,
    errors,
    warnings
  };
};